import _ from 'lodash'
import moment, { Moment } from 'moment'
import { createSelector } from 'reselect'

import { DEFAULT_DATE_FORMAT } from 'constants/date'
import { Option } from 'constants/models'
import { Child } from 'services/child/constants'
import { REVIEW_MEDICATION_STATUSES } from 'services/reviews/constants'

import { convertTimeDuration, floatToHoursAndMinutes } from 'utils/date'
import { createTypeFromEnumValues } from 'utils/typescript'

import { AddMedicationFormVales } from 'module/Safeguarding/MedicationForm/Add/components/AddForm'

import { RootState } from 'core/reducers'

import { BEFORE_AFTER_EATING, BEFORE_AFTER_EATING_OPTIONS, FREQUENCIES, MEDICATION_FORM_STATUSES } from '../constants'
import { MedicationForm } from '../models'

export const getMedicationFormsSelector = (state: RootState) => state.medicationForms

export const getMedicationFormsSingleSelector = createSelector(
  [getMedicationFormsSelector],
  (state) => state.single.data,
)

export const getInitialValues = (
  data: MedicationForm,
  childContextData?: Child,
): AddMedicationFormVales => {
  if (!data) {
    return {
      child: childContextData ? {
        avatar: childContextData.photo,
        label: childContextData.name,
        value: childContextData.id,
      } : undefined,
      medicationDetails: {
        frequency: FREQUENCIES.DAILY,
        storeOnSite: false,
        timesOfAdministration: [{}],
      },
    }
  }

  const {
    child,
    endDate,
    medicationDetails,
    otherNotes,
    reviewDate,
    startDate,
  } = data
  const {
    beforeOrAfterEating,
    dose,
    expiryDate,
    frequency,
    howLongBeforeOrAfterEating,
    id,
    medication,
    methodOfAdministration,
    reason,
    sideEffects,
    storageInstructions,
    storeOnSite,
    timesOfAdministration,
  } = medicationDetails

  let beforeAfterEatingHours
  let beforeAfterEatingMinutes

  if (howLongBeforeOrAfterEating) {
    const { hours, minutes } = floatToHoursAndMinutes(convertTimeDuration(
      +howLongBeforeOrAfterEating,
      'milliseconds',
      'hours',
    ))

    beforeAfterEatingHours = hours
    beforeAfterEatingMinutes = minutes
  }

  return {
    child: {
      avatar: child?.photo,
      label: child?.name,
      value: child?.id,
    },
    endDate: endDate as Date,
    medicationDetails: {
      beforeAfterEatingHours,
      beforeAfterEatingMinutes,
      beforeOrAfterEating: _.find(BEFORE_AFTER_EATING_OPTIONS, { value: beforeOrAfterEating }) as Option,
      dose,
      expiryDate: expiryDate as Date,
      frequency,
      id,
      medication,
      methodOfAdministration,
      reason,
      sideEffects,
      storageInstructions,
      storeOnSite,
      timesOfAdministration: timesOfAdministration?.length
        ? timesOfAdministration.map((time) => ({ time }))
        : [{}],
    },
    otherNotes,
    reviewDate: reviewDate as Date,
    startDate: startDate as Date,
  }
}

export const getBody = (
  fields: AddMedicationFormVales,
  status: createTypeFromEnumValues<MEDICATION_FORM_STATUSES>,
  isLongTerm: boolean,
): MedicationForm => {
  if (!fields) {
    return null
  }

  const { child, endDate, medicationDetails, otherNotes, reviewDate, startDate } = fields
  const {
    beforeAfterEatingHours,
    beforeAfterEatingMinutes,
    beforeOrAfterEating,
    dose,
    expiryDate,
    frequency,
    id,
    medication,
    methodOfAdministration,
    reason,
    sideEffects,
    storageInstructions,
    storeOnSite,
    timesOfAdministration,
  } = medicationDetails

  let finalBeforeAfterEating

  if (
    beforeOrAfterEating?.value === BEFORE_AFTER_EATING.AFTER_EATING
    || beforeOrAfterEating?.value === BEFORE_AFTER_EATING.BEFORE_EATING
  ) {
    finalBeforeAfterEating = convertTimeDuration(+(beforeAfterEatingHours || ''), 'h')
    finalBeforeAfterEating += convertTimeDuration(+(beforeAfterEatingMinutes || ''))
  }

  return {
    child: {
      id: child.value as number,
    },
    endDate: endDate ? `${moment(endDate).format(DEFAULT_DATE_FORMAT)} 23:59:59` : undefined,
    medicationDetails: {
      beforeOrAfterEating: beforeOrAfterEating?.value,
      dose,
      expiryDate: expiryDate ? moment(expiryDate).format(DEFAULT_DATE_FORMAT) : undefined,
      frequency,
      howLongBeforeOrAfterEating: finalBeforeAfterEating,
      id,
      medication,
      methodOfAdministration,
      reason,
      sideEffects,
      storageInstructions: storeOnSite ? storageInstructions : undefined,
      storeOnSite,
      timesOfAdministration: frequency === FREQUENCIES.DAILY && timesOfAdministration?.length
        ? _.map(_.filter(timesOfAdministration, ({ time }) => !!time), ({ time }) => time)
        : null,
      type: 'medicationForm',
    },
    otherNotes,
    reviewDate: isLongTerm && reviewDate
      ? moment(reviewDate).format(DEFAULT_DATE_FORMAT)
      : undefined,
    startDate: startDate ? moment(startDate).format(DEFAULT_DATE_FORMAT) : undefined,
    status,
  }
}

export const isLongTermMedicationForm = (
  startDate?: string | number | Date | Moment,
  endDate?: string | number | Date | Moment,
): boolean => {
  if (!startDate || !endDate) {
    return false
  }

  const startDateMoment = moment(startDate).startOf('day')
  const endDateMoment = moment(endDate).endOf('day')

  return 30 <= endDateMoment.diff(startDateMoment, 'days')
}

export const getInitialFormValues = createSelector(
  [getMedicationFormsSingleSelector], (data) => {
    if (!data) {
      return {
        reviewDate: moment(),
      }
    }

    const { endDate, nextReviewDate, startDate } = data

    const isLongTerm = isLongTermMedicationForm(startDate, endDate)

    return {
      endDate: moment(endDate),
      reviewDate: isLongTerm ? moment(nextReviewDate) : moment(endDate),
      type: !isLongTerm ? REVIEW_MEDICATION_STATUSES.END_MEDICATION : undefined,
    }
  },
)

export const getMedicationFormExportCriteria = ({ id, membershipId }: {
  id: number
  membershipId: number
}) => ({
  object: { id },
  recipient: { id: membershipId },
})
